import React, { useState } from 'react';
import type { ActionData, ActionType } from '../../services/chatService';
import RichTextDisplay from '../RichTextDisplay';

interface ActionConfirmationProps {
  messageId: string;
  actionType: ActionType;
  actionData: ActionData;
  onApprove: (messageId: string) => Promise<void>;
  onReject: (messageId: string) => Promise<void>;
}

export function ActionConfirmation({
  messageId,
  actionType,
  actionData,
  onApprove,
  onReject
}: ActionConfirmationProps): React.ReactElement {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await onApprove(messageId);
    } catch (error) {
      console.error('Failed to approve action:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    try {
      await onReject(messageId);
    } catch (error) {
      console.error('Failed to reject action:', error);
    } finally {
      setIsRejecting(false);
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'functional':
        return 'bg-blue-100 text-blue-800';
      case 'non-functional':
        return 'bg-purple-100 text-purple-800';
      case 'technical':
        return 'bg-indigo-100 text-indigo-800';
      case 'business':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTypeLabel = (type?: string) => {
    if (!type) return 'Unknown';
    return type.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="mt-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h4 className="text-sm font-semibold text-gray-900">
            {actionType === 'create_requirement' ? 'Create New Requirement' : 'Update Requirement'}
          </h4>
        </div>
        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
          Pending Approval
        </span>
      </div>

      <div className="space-y-3 mb-4">
        {actionData.data.title && (
          <div>
            <label className="text-xs font-medium text-gray-600">Title</label>
            <p className="text-sm text-gray-900 font-medium">{actionData.data.title}</p>
          </div>
        )}

        {actionData.data.description && (
          <div>
            <label className="text-xs font-medium text-gray-600">Description</label>
            <RichTextDisplay content={actionData.data.description} />
          </div>
        )}

        <div className="flex items-center gap-3">
          {actionData.data.requirement_type && (
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Type</label>
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${getTypeColor(actionData.data.requirement_type)}`}>
                {formatTypeLabel(actionData.data.requirement_type)}
              </span>
            </div>
          )}

          {actionData.data.priority && (
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Priority</label>
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${getPriorityColor(actionData.data.priority)}`}>
                {actionData.data.priority.charAt(0).toUpperCase() + actionData.data.priority.slice(1)}
              </span>
            </div>
          )}
        </div>

        {actionData.changes_explanation && (
          <div className="pt-2 border-t border-blue-200">
            <label className="text-xs font-medium text-gray-600">Changes</label>
            <p className="text-sm text-gray-700">{actionData.changes_explanation}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-3 border-t border-blue-200">
        <button
          onClick={handleReject}
          disabled={isRejecting || isApproving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRejecting ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Rejecting...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Reject
            </>
          )}
        </button>

        <button
          onClick={handleApprove}
          disabled={isApproving || isRejecting}
          className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isApproving ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Approving...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Approve
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default ActionConfirmation;
